import React from 'react';
import { ProductDetail } from '../../../../types/ProductTypes';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { SidebarCard, SidebarTitle, SidebarContent, SidebarItem } from '../../Sidebar.styles';
import {
    CompactInput35,
    CompactInput55,
    CompactInput70,
    CompactRangeInput40,
    CompactRangeInput45,
    RangeInputWrapper,
    UnitLabel35,
    UnitLabel45,
    CompactSelect,
} from '../../EditableFields.styles';

// Yes/No/Unknown enum values (matching old implementation)
const YES_NO_UNKNOWN_OPTIONS = [
    { value: 0, label: 'Unknown' },
    { value: 1, label: 'Yes' },
    { value: 2, label: 'No' },
];

// Origin options (matching old implementation)
const ORIGIN_OPTIONS = [
    'Petroleum',
    'Bio-mass (vegetable)',
    'Bio-mass (animal)',
    'Coal',
    'Inorganic (from petroleum/LNG)',
    'Inorganic (phosphate)',
    'Inorganic (brine)',
    'Inorganic (potassium salt)',
    'Check',
];

// Helper to map Yes/No/Unknown enum to string
const yesNoUnknownToString = (value: number | null | undefined): string => {
    switch (value) {
        case 1:
            return 'Yes';
        case 2:
            return 'No';
        default:
            return 'Unknown';
    }
};

interface PhysicalPropertiesSectionProps {
    product: ProductDetail;
}

const PhysicalPropertiesSection: React.FC<PhysicalPropertiesSectionProps> = ({ product }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const showEditUI = canEdit && isInEditMode;

    if (!product.physicalData) {
        return null;
    }

    const pd = product.physicalData;

    // Helper function to render range presentation (matching old implementation)
    const renderRange = (min: number | null | undefined, max: number | null | undefined, unit: string): string => {
        const hasMin = min !== null && min !== undefined;
        const hasMax = max !== null && max !== undefined;

        if (!hasMin && !hasMax) return ' ';
        if (!hasMin) return `< ${max} ${unit}`;
        if (!hasMax) return `> ${min} ${unit}`;
        if (min === max) return `${min} ${unit}`;
        return `${min} ${unit} / ${max} ${unit}`;
    };

    // Helper for temperature ranges (extracts value from object)
    const renderTempRange = (min: any, max: any): string => {
        const minVal = min?.value;
        const maxVal = max?.value;
        const hasMin = minVal !== null && minVal !== undefined;
        const hasMax = maxVal !== null && maxVal !== undefined;

        if (!hasMin && !hasMax) return ' ';
        if (!hasMin) return `< ${maxVal}°C`;
        if (!hasMax) return `> ${minVal}°C`;
        if (minVal === maxVal) return `${minVal}°C`;
        return `${minVal}°C / ${maxVal}°C`;
    };

    // Calculate Persistent Floater based on USCG group and boiling point
    // (matching old implementation: getCalculatedValueForPersistentFloater)
    const getCalculatedPersistentFloater = (): string => {
        const uscgGroup = product.regulations?.uscgGroup;
        const boilingPointMin = pd.boilingPointRange?.minValue?.value;
        const boilingPointMax = pd.boilingPointRange?.maxValue?.value;

        // Check if USCG group is defined
        if (!uscgGroup || uscgGroup.number === undefined || uscgGroup.number === null) {
            return 'No';
        }

        // Check if it's a possible floater group (30, 31, 32, 33)
        const floaterGroups = [30, 31, 32, 33];
        if (!floaterGroups.includes(uscgGroup.number)) {
            return 'No';
        }

        // Check if boiling point is over 340
        if (
            (boilingPointMin !== null && boilingPointMin !== undefined && boilingPointMin >= 340) ||
            (boilingPointMax !== null && boilingPointMax !== undefined && boilingPointMax >= 340)
        ) {
            return 'Yes';
        }

        return 'No';
    };

    return (
        <SidebarCard>
            <SidebarTitle>Physical Properties</SidebarTitle>
            <SidebarContent>
                {/* Litre Weight */}
                {pd.litreWeight && (
                    <SidebarItem>
                        <label>Litre Weight ({pd.litreWeightReferenceTemperature?.value || 20}°C):</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactInput55 type="text" value={pd.litreWeight.value || ''} onChange={() => {}} />
                                <UnitLabel35>kg/L</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{pd.litreWeight.value} kg/L</span>
                        )}
                    </SidebarItem>
                )}

                {/* Litre Weight Reference Temperature - Edit Mode Only */}
                {showEditUI && pd.litreWeight && (
                    <SidebarItem>
                        <label>Ref. Temp.:</label>
                        <RangeInputWrapper>
                            <CompactInput35 type="text" value={pd.litreWeightReferenceTemperature?.value || ''} onChange={() => {}} />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    </SidebarItem>
                )}

                {pd.litreWeightCorrectionFactor !== null && (
                    <SidebarItem>
                        <label>Correction Factor:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactInput70 type="text" value={pd.litreWeightCorrectionFactor || ''} onChange={() => {}} />
                                <UnitLabel35></UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{pd.litreWeightCorrectionFactor || ' '}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Melting Point */}
                {pd.meltingPointRange && (
                    <SidebarItem>
                        <label>Melting Point:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45 type="text" value={pd.meltingPointRange.minValue?.value ?? ''} onChange={() => {}} />
                                <span>-</span>
                                <CompactRangeInput45 type="text" value={pd.meltingPointRange.maxValue?.value ?? ''} onChange={() => {}} />
                                <UnitLabel35>°C</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderTempRange(pd.meltingPointRange.minValue, pd.meltingPointRange.maxValue)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Boiling Point */}
                {pd.boilingPointRange && (
                    <SidebarItem>
                        <label>Boiling Point:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45 type="text" value={pd.boilingPointRange.minValue?.value ?? ''} onChange={() => {}} />
                                <span>-</span>
                                <CompactRangeInput45 type="text" value={pd.boilingPointRange.maxValue?.value ?? ''} onChange={() => {}} />
                                <UnitLabel35>°C</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderTempRange(pd.boilingPointRange.minValue, pd.boilingPointRange.maxValue)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Flash Point */}
                {pd.flashPointRange && (
                    <SidebarItem>
                        <label>Flash Point:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45 type="text" value={pd.flashPointRange.minValue?.value ?? ''} onChange={() => {}} />
                                <span>-</span>
                                <CompactRangeInput45 type="text" value={pd.flashPointRange.maxValue?.value ?? ''} onChange={() => {}} />
                                <UnitLabel35>°C</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderTempRange(pd.flashPointRange.minValue, pd.flashPointRange.maxValue)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Viscosity */}
                {pd.viscosityRange && (
                    <SidebarItem>
                        <label>Viscosity <i>{pd.viscosityReferenceTemperature?.value ?? ''} °C</i>:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45 type="text" value={pd.viscosityRange.minValue ?? ''} onChange={() => {}} />
                                <span>-</span>
                                <CompactRangeInput45 type="text" value={pd.viscosityRange.maxValue ?? ''} onChange={() => {}} />
                                <UnitLabel45>mPa.s</UnitLabel45>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.viscosityRange.minValue, pd.viscosityRange.maxValue, 'mPa.s')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Viscosity Reference Temperature - Edit Mode Only */}
                {showEditUI && pd.viscosityRange && (
                    <SidebarItem>
                        <label>Ref. Temp.:</label>
                        <RangeInputWrapper>
                            <CompactInput35 type="text" value={pd.viscosityReferenceTemperature?.value || ''} onChange={() => {}} />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    </SidebarItem>
                )}

                {/* Vapour Pressure */}
                {pd.vapourPressureRange && (
                    <SidebarItem>
                        <label>Vapour Pressure:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45 type="text" value={pd.vapourPressureRange.minValue ?? ''} onChange={() => {}} />
                                <span>-</span>
                                <CompactRangeInput45 type="text" value={pd.vapourPressureRange.maxValue ?? ''} onChange={() => {}} />
                                <UnitLabel35>kPa</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.vapourPressureRange.minValue, pd.vapourPressureRange.maxValue, 'kPa')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Vapour Pressure Reference Temperature - Edit Mode Only */}
                {showEditUI && pd.vapourPressureRange && (
                    <SidebarItem>
                        <label>Ref. Temp.:</label>
                        <RangeInputWrapper>
                            <CompactInput35 type="text" value={pd.vapourPressureReferenceTemperature?.value || ''} onChange={() => {}} />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    </SidebarItem>
                )}

                {/* Odor Threshold */}
                {pd.odorThreshold !== null && (
                    <SidebarItem>
                        <label>Odor Threshold:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactInput55 type="text" value={pd.odorThreshold ?? ''} onChange={() => {}} />
                                <UnitLabel35>ppm</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.odorThreshold, pd.odorThreshold, 'ppm')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* PH */}
                {pd.phRange && (
                    <SidebarItem>
                        <label>PH:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput40 type="text" value={pd.phRange.minValue ?? ''} onChange={() => {}} />
                                <span>-</span>
                                <CompactRangeInput40 type="text" value={pd.phRange.maxValue ?? ''} onChange={() => {}} />
                                <UnitLabel35></UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{renderRange(pd.phRange.minValue, pd.phRange.maxValue, '')}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Inhibited - read-only, comes from category */}
                <SidebarItem>
                    <label>Inhibited:</label>
                    <span>{product.category?.isInhibited ? 'Yes' : 'No'}</span>
                </SidebarItem>

                {/* Insurance Persistent Floater - dropdown in edit mode */}
                <SidebarItem>
                    <label>Insurance Persistent Floater:</label>
                    {showEditUI ? (
                        <CompactSelect title="Insurance Persistent Floater" value={product.regulations?.persistentFloater ?? 0} onChange={() => {}}>
                            {YES_NO_UNKNOWN_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{yesNoUnknownToString(product.regulations?.persistentFloater)}</span>
                    )}
                </SidebarItem>

                {/* Calculated Persistent Floater - only shown in edit mode */}
                {showEditUI && (
                    <SidebarItem>
                        <label>Calc. Persistent Floater:</label>
                        <span>{getCalculatedPersistentFloater()}</span>
                    </SidebarItem>
                )}

                {/* Origin - dropdown in edit mode */}
                {(pd.origin || showEditUI) && (
                    <SidebarItem>
                        <label>Origin:</label>
                        {showEditUI ? (
                            <CompactSelect title="Origin" value={pd.origin || ''} onChange={() => {}}>
                                <option value="">-- Select --</option>
                                {ORIGIN_OPTIONS.map((origin) => (
                                    <option key={origin} value={origin}>
                                        {origin}
                                    </option>
                                ))}
                            </CompactSelect>
                        ) : (
                            <span>{pd.origin}</span>
                        )}
                    </SidebarItem>
                )}
            </SidebarContent>
        </SidebarCard>
    );
};

export default PhysicalPropertiesSection;
